import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Formik, Field } from 'formik'
import { useCmsBlock, useLogin } from 'lib/core/hooks'
import { Button, Form, Toast, Alert } from 'lib/ui'
import { InterceptBlurEvent } from 'lib/ui/components/Form/types'
import { useCheckout } from 'lib/modules/checkout'
import { schema, validation, Values } from './schema'
import { Container } from './styled'

interface Props {
  onCancel?: () => void
  interceptBlur?: (e: InterceptBlurEvent<string>) => void
  cmsBlockId?: string
}

const DEFAULT_CMS_BLOCK_ID = 'checkout-login-message'

export const Login = ({ interceptBlur, onCancel, cmsBlockId = DEFAULT_CMS_BLOCK_ID }: Props) => {
  const intl = useIntl()
  const { checkoutSchema } = useCheckout()
  const { content, isLoading } = useCmsBlock({ id: cmsBlockId })

  const { login, error, isFetching } = useLogin({
    onFailure: () => {
      Toast.toastFailure({ messageId: error })
    }
  })

  const initialValues = {
    ...schema,
    email: checkoutSchema?.email ?? ''
  }

  const handleSubmit = (values: Values) => {
    login({
      email: values.email,
      password: values.password
    })
  }

  return (
    <Container>
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validation({ intl })}
      >
        <Form.Wrapper>
          <h2>
            {intl.formatMessage({
              id: 'checkout.login.title'
            })}
          </h2>

          <Alert>
            {isLoading ? (
              <p>...</p>
            ) : (
              <>
                {content ? (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: content
                    }}
                  />
                ) : (
                  intl.formatMessage({
                    id: 'checkout.login.message'
                  })
                )}
              </>
            )}
          </Alert>

          <Field
            name='email'
            label={intl.formatMessage({
              id: 'customer.account.login.label.email'
            })}
            component={Form.Input}
            autoFocus
            interceptBlur={interceptBlur}
            isRequired
            hasAutoPlaceholder
          />

          <Field
            name='password'
            label={intl.formatMessage({
              id: 'customer.account.login.label.password'
            })}
            component={Form.Input}
            type='password'
            isRequired
          />

          <Form.Actions.Wrapper autoArrangeWidth={false}>
            <Button
              type='submit'
              isLoading={isFetching}
            >
              <FormattedMessage
                id='action.login'
              />
            </Button>

            <Button
              skin='text'
              type='button'
              onClick={onCancel}
            >
              <FormattedMessage
                id='checkout.action.continueAsGuest'
              />
            </Button>
          </Form.Actions.Wrapper>
        </Form.Wrapper>
      </Formik>
    </Container>
  )
}
